import { ClubMembersExportRun, pollClubMembersExport, startClubMembersExport } from '@/services/club'
import { serverErrorMessage } from '@/services/httpService'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

/** How long to leave between polls. Long enough not to hammer, short enough to feel answered. */
const POLL_INTERVAL_MS = 2000
/** Give up after this long. A run that has not finished by now has almost certainly failed. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000
/** Polls in a row that may get no answer at all before the export is reported as lost. */
const MAX_DROPPED_POLLS = 3

/** A rejection with no HTTP response behind it: the network dropped, not the server refusing. */
const isConnectionError = (error: unknown) => !(error as { response?: unknown })?.response

/**
 * Exports a club's member list as a CSV, for both member screens.
 *
 * The file is built in the background and polled for, rather than waited on in one request: a large
 * club's export outlasts the load balancer's patience, so the single request used to fail in the
 * browser after the server had already built the file — and each retry spent the director's export
 * limit.
 *
 * Returns `downloadRef` for a hidden `<a>` the caller renders. The download goes through an in-page
 * anchor rather than `window.open`, which browsers block when called from an async callback.
 */
export const useExportClubMembers = (clubId: string | number | undefined) => {
  const { t } = useTranslation()
  const [isExporting, setIsExporting] = useState(false)
  const downloadRef = useRef<HTMLAnchorElement>(null)

  // Polling outlives a render, so it has to be stoppable from a cleanup — otherwise leaving the page
  // mid-export keeps a timer running and starts a download on a page the director is no longer on.
  const cancelled = useRef(false)
  useEffect(() => {
    cancelled.current = false
    return () => {
      cancelled.current = true
    }
  }, [])

  /** One poll. A poll that got no answer resolves to `undefined` until too many in a row have. */
  const pollOnce = async (id: string | number, runId: string, dropped: { count: number }) => {
    try {
      const run = await pollClubMembersExport(id, runId)
      dropped.count = 0
      return run
    } catch (error) {
      if (!isConnectionError(error) || ++dropped.count >= MAX_DROPPED_POLLS) throw error
      return undefined
    }
  }

  /** Polls until the run finishes. Resolves to `undefined` if the deadline passes or the page is left. */
  const waitForExport = async (id: string | number, runId: string): Promise<ClubMembersExportRun | undefined> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS
    const dropped = { count: 0 }
    while (!cancelled.current && Date.now() <= deadline) {
      // Waited BEFORE the first poll on purpose: the job has only just been queued.
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      if (cancelled.current) return undefined
      const run = await pollOnce(id, runId, dropped)
      if (run && run.status !== 'RUNNING') return run
    }
    return undefined
  }

  const download = (url: string) => {
    if (!downloadRef.current) return
    downloadRef.current.href = url
    downloadRef.current.click()
    downloadRef.current.href = ''
  }

  const exportMembers = async () => {
    if (!clubId || isExporting) return
    setIsExporting(true)
    try {
      const run = await waitForExport(clubId, await startClubMembersExport(clubId))
      if (cancelled.current) return
      if (run?.status === 'COMPLETED' && run.s3Url) download(run.s3Url)
      else toast.error(run ? t('common.exportFailed') : t('common.exportTimedOut'))
    } catch (error) {
      if (cancelled.current) return
      // The server's own sentence when it sent one — that is how a director learns they have hit
      // the hourly or daily export limit.
      toast.error(
        isConnectionError(error)
          ? t('common.exportConnectionLost')
          : serverErrorMessage(error, t('common.exportFailed'))
      )
    } finally {
      if (!cancelled.current) setIsExporting(false)
    }
  }

  return { exportMembers, isExporting, downloadRef }
}
